import {Combobox, useListCollection} from "@chakra-ui/react";
import {forwardRef} from "react";
import useTranslate from "platformed-components/i18n/useTranslate";
import {Portal} from "../Portal";
import {ScalarValueInputProps} from "./types";

/**
 * Boolean input component
 *
 * Features:
 * - Yes/No dropdown selection
 * - Returns true, false, or null
 */
const BooleanInput = forwardRef<HTMLInputElement, ScalarValueInputProps<boolean>>(
  ({value, onChange, placeholder, disabled = false}, ref) => {
    const t = useTranslate();
    const effectivePlaceholder = placeholder ?? t("c.Yes_No[slash]");
    const options = [
      {label: t("c.Yes"), value: "true"},
      {label: t("c.No"), value: "false"},
    ];

    const {collection} = useListCollection({initialItems: options});
    const stringValue = typeof value === "boolean" ? String(value) : undefined;
    const selectedLabel = stringValue !== undefined ? options.find(opt => opt.value === stringValue)?.label : undefined;

    return (
      <Combobox.Root
        collection={collection}
        value={stringValue !== undefined ? [stringValue] : []}
        inputValue={selectedLabel}
        onValueChange={e => {
          const selected = e.value[0];
          if (selected === undefined) {
            onChange(null);
          } else {
            onChange(selected === "true");
          }
        }}
        openOnClick
        disabled={disabled}
      >
        <Combobox.Control>
          <Combobox.Input ref={ref} placeholder={effectivePlaceholder} disabled={disabled} />
          <Combobox.IndicatorGroup>
            <Combobox.Trigger />
          </Combobox.IndicatorGroup>
        </Combobox.Control>
        <Portal>
          <Combobox.Positioner>
            <Combobox.Content>
              {collection.items.map(item => (
                <Combobox.Item item={item} key={String(item.value)}>
                  {item.label}
                  <Combobox.ItemIndicator />
                </Combobox.Item>
              ))}
            </Combobox.Content>
          </Combobox.Positioner>
        </Portal>
      </Combobox.Root>
    );
  },
);

BooleanInput.displayName = "BooleanInput";

export default BooleanInput;
